import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import NovaPoshtaAutoComplete from "./NovaPoshtaAutoComplete";
import styles from "./CheckoutForm.module.css";
import { useCustomer } from "../context/CustomerContext";
import { analytics } from "../utils/analytics";
import { getCustomerRedeemBonusKopecks } from "../utils/loyalty";
import {
  CHECKOUT_DELIVERY_METHOD_OPTIONS,
  DELIVERY_METHOD_TYPES,
  getDeliveryMethodType,
  getDeliveryMethodValue,
  isBranchDelivery,
  isCourierDelivery,
  isKyivCourierDelivery,
  isNovaPoshtaCourierDelivery,
  isNovaPoshtaLockerDelivery,
} from "../api/checkoutDeliverySettingsApi";
import { useDeliverySettings } from "../context/DeliverySettingsContext";
import {
  CHECKOUT_PAYMENT_METHODS,
  normalizeCheckoutPaymentMethod,
} from "../config/paymentMethods";
import { validateNovaPostCheckoutDelivery } from "../api/novaPoshtaDirectoryApi";
import {
  buildNovaPoshtaDelivery,
  createEmptyNovaPoshtaDelivery,
  divisionFromSelection,
  formatNovaPostCityLabel,
  formatNovaPostDivisionLabel,
  getNovaPoshtaDeliveryMethod,
  settlementFromSelection,
  validateNovaPoshtaCheckout,
} from "../utils/novaPoshtaDelivery";

const STORAGE_KEY = "checkoutData";

// Функція завантаження з localStorage
const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const savedData = raw ? JSON.parse(raw) : {};

    return {
      ...savedData,
      deliveryMethod: getDeliveryMethodValue(savedData.deliveryMethod),
      paymentMethod: normalizeCheckoutPaymentMethod(savedData.paymentMethod),
    };
  } catch {
    return {};
  }
};

const CheckoutForm = ({ cartItems, totalAmount, loyalty = null, stockInsufficient = false }) => {
  const navigate = useNavigate();
  const { customer, loading } = useCustomer();

  // --- Очищаємо localStorage при першому відкритті (МОЖНА ЗАЛИШИТИ або прибрати) ---
  // useEffect(() => {
  //   localStorage.removeItem(STORAGE_KEY);
  // }, []);

  // --- Основний стейт форми ---
  const [formData, setFormData] = useState({
    customerName: "",
    phone: "+380",
    email: "",
    deliveryMethod: "",
    paymentMethod: "",
    city: "",
    branchNumber: "",
    novaPostCityRef: "",
    novaPostBranchRef: "",
    novaPostBranchNumber: "",
    novaPostPointType: "",
    novaPoshtaDelivery: undefined,
    street: "",
    buildingNumber: "",
    postalCode: "",
    apartmentNumber: "",
    entranceNumber: "",
    floor: "",
    courierNote: "",
    ...loadFromStorage(),
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [deliveryValidationMessage, setDeliveryValidationMessage] = useState("");
  const [validatingDelivery, setValidatingDelivery] = useState(false);
  const { settings: deliverySettings } = useDeliverySettings();
  const deliveryMethods = deliverySettings.deliveryMethods;

  const enabledDeliveryOptions = useMemo(
    () =>
      CHECKOUT_DELIVERY_METHOD_OPTIONS.filter(
        (option) => deliveryMethods[option.settingKey]
      ),
    [deliveryMethods]
  );
  const enabledDeliveryValues = useMemo(
    () => new Set(enabledDeliveryOptions.map((option) => option.value)),
    [enabledDeliveryOptions]
  );
  const firstEnabledDeliveryMethod = enabledDeliveryOptions[0]?.value || "";

  // --- АВТОЗАПОВНЕННЯ з customer (і оновлення після зміни основної адреси!) ---
  useEffect(() => {
    if (!customer) return;

    const fullName = [customer.lastName, customer.firstName]
      .filter(Boolean)
      .join(" ");
    const mainAddress =
      (customer.addresses && customer.addresses.find((a) => a.isMain)) ||
      (customer.addresses && customer.addresses[0]) ||
      {};

    setFormData((f) => {
      // Якщо основна адреса у профілі відрізняється від форми — перезаписуємо!
      const needUpdateAddress =
        f.city !== (mainAddress.city || "") ||
        f.branchNumber !== (mainAddress.branchNumber || "");

      return {
        ...f,
        customerName: f.customerName || fullName,
        // Телефон підтягуємо, якщо поле порожнє або "+380"
        phone:
          !f.phone || f.phone === "+380" ? customer.phone || "+380" : f.phone,
        email: f.email || customer.email || "",
        deliveryMethod: f.deliveryMethod || firstEnabledDeliveryMethod,
        city: needUpdateAddress ? mainAddress.city || "" : f.city,
        branchNumber: needUpdateAddress
          ? mainAddress.branchNumber || ""
          : f.branchNumber,
        novaPostCityRef: needUpdateAddress
          ? mainAddress.novaPostCityRef || ""
          : f.novaPostCityRef,
        novaPostBranchRef: needUpdateAddress
          ? mainAddress.novaPostBranchRef || ""
          : f.novaPostBranchRef,
        novaPostBranchNumber: needUpdateAddress
          ? mainAddress.novaPostBranchNumber || ""
          : f.novaPostBranchNumber,
        novaPoshtaDelivery: needUpdateAddress
          ? mainAddress.novaPoshtaDelivery || f.novaPoshtaDelivery
          : f.novaPoshtaDelivery,
      };
    });
  }, [customer, firstEnabledDeliveryMethod]);

  useEffect(() => {
    setFormData((f) => {
      if (f.deliveryMethod && enabledDeliveryValues.has(f.deliveryMethod)) {
        return f;
      }

      if (f.deliveryMethod === firstEnabledDeliveryMethod) {
        return f;
      }

      return {
        ...f,
        deliveryMethod: firstEnabledDeliveryMethod,
      };
    });
  }, [enabledDeliveryValues, firstEnabledDeliveryMethod]);

  useEffect(() => {
    setFormData((f) => {
      const deliveryMethodType = getDeliveryMethodType(f.deliveryMethod);
      const nextNovaPostMethod = getNovaPoshtaDeliveryMethod(f.deliveryMethod);
      const currentCity = {
        id: Number(f.novaPoshtaDelivery?.city?.id) || null,
        ref: f.novaPoshtaDelivery?.city?.ref || f.novaPostCityRef || "",
        name: f.novaPoshtaDelivery?.city?.name || f.city || "",
        regionName: f.novaPoshtaDelivery?.city?.regionName || "",
        areaName: f.novaPoshtaDelivery?.city?.areaName || "",
      };

      if (isBranchDelivery(f.deliveryMethod)) {
        const nextPointType = isNovaPoshtaLockerDelivery(f.deliveryMethod)
          ? "locker"
          : "branch";
        const methodChanged =
          f.novaPoshtaDelivery?.method !== nextNovaPostMethod;
        const nextDelivery = methodChanged
          ? {
              ...createEmptyNovaPoshtaDelivery(nextNovaPostMethod),
              city: currentCity,
            }
          : f.novaPoshtaDelivery;
        return {
          ...f,
          ...(methodChanged
            ? {
                branchNumber: "",
                novaPostBranchRef: "",
                novaPostBranchNumber: "",
              }
            : {}),
          novaPostPointType: nextPointType,
          novaPoshtaDelivery: nextDelivery,
          street: "",
          buildingNumber: "",
          postalCode: "",
          apartmentNumber: "",
          entranceNumber: "",
          floor: "",
          courierNote: "",
        };
      }
      if (deliveryMethodType === DELIVERY_METHOD_TYPES.NOVA_POSHTA_COURIER) {
        const methodChanged = f.novaPoshtaDelivery?.method !== "courier";
        return {
          ...f,
          branchNumber: "",
          novaPostBranchRef: "",
          novaPostBranchNumber: "",
          novaPostPointType: "",
          novaPoshtaDelivery: methodChanged
            ? {
                ...createEmptyNovaPoshtaDelivery("courier"),
                city: currentCity,
              }
            : f.novaPoshtaDelivery,
        };
      }
      if (deliveryMethodType === DELIVERY_METHOD_TYPES.KYIV_COURIER) {
        return {
          ...f,
          branchNumber: "",
          novaPostBranchRef: "",
          novaPostBranchNumber: "",
          novaPostPointType: "",
          novaPoshtaDelivery: undefined,
          city: "Київ, Київська область",
        };
      }
      return { ...f, novaPoshtaDelivery: undefined };
    });
  }, [formData.deliveryMethod]);

  // --- Зберігаємо форму в localStorage при кожній зміні ---
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  const updateField = (key) => (e) => {
    const val = e.target.value;
    setFormData((f) => ({ ...f, [key]: val }));
  };

  const validateForm = useCallback(() => {
    const errs = {};
    const {
      customerName,
      phone,
      deliveryMethod,
      paymentMethod,
      city,
      branchNumber,
      street,
      buildingNumber,
      postalCode,
    } = formData;
    const deliveryMethodType = getDeliveryMethodType(deliveryMethod);

    if (!customerName) errs.customerName = true;
    if (!phone) errs.phone = true;
    if (!deliveryMethod || !enabledDeliveryValues.has(deliveryMethod)) {
      errs.deliveryMethod = true;
    }
    if (!paymentMethod) errs.paymentMethod = true;

    Object.assign(errs, validateNovaPoshtaCheckout(formData));
    if (errs.novaPostCityId) errs.city = true;

    if (
      isBranchDelivery(deliveryMethod) &&
      (!city || !branchNumber)
    ) {
      if (!city) errs.city = true;
      if (!branchNumber) errs.branchNumber = true;
    }
    if (
      deliveryMethodType === DELIVERY_METHOD_TYPES.NOVA_POSHTA_COURIER &&
      (!city || !street || !buildingNumber || !postalCode)
    ) {
      if (!city) errs.city = true;
      if (!street) errs.street = true;
      if (!buildingNumber) errs.buildingNumber = true;
      if (!postalCode) errs.postalCode = true;
    }
    if (
      deliveryMethodType === DELIVERY_METHOD_TYPES.KYIV_COURIER &&
      (!street || !buildingNumber)
    ) {
      if (!street) errs.street = true;
      if (!buildingNumber) errs.buildingNumber = true;
    }

    setValidationErrors(errs);
    setShowErrorMessage(Object.keys(errs).length > 0);
    return Object.keys(errs).length === 0;
  }, [formData, enabledDeliveryValues]);

  const handleSubmitOrder = async () => {
    if (!validateForm()) return;

    const novaPoshtaDelivery = buildNovaPoshtaDelivery(formData);
    if (novaPoshtaDelivery) {
      setValidatingDelivery(true);
      setDeliveryValidationMessage("");
      try {
        await validateNovaPostCheckoutDelivery({
          deliveryMethod: formData.deliveryMethod,
          novaPoshtaDelivery,
        });
      } catch (error) {
        const field = error?.response?.data?.field || "novaPoshtaDelivery";
        setValidationErrors((current) => ({ ...current, [field]: true }));
        setDeliveryValidationMessage(
          error?.response?.data?.message ||
            "Не вдалося перевірити дані доставки Nova Post."
        );
        setShowErrorMessage(true);
        setValidatingDelivery(false);
        return;
      }
      setValidatingDelivery(false);
    }

    let customerDetails = {
      name: formData.customerName,
      phone: formData.phone,
      email: formData.email,
      deliveryMethod: formData.deliveryMethod,
      paymentMethod: formData.paymentMethod,
      ...(novaPoshtaDelivery ? { novaPoshtaDelivery } : {}),
    };

    // Додаємо адресні поля залежно від методу доставки
    if (isBranchDelivery(formData.deliveryMethod)) {
      customerDetails.city = formData.city;
      customerDetails.branchNumber = formData.branchNumber;
      customerDetails.novaPostCityRef = formData.novaPostCityRef;
      customerDetails.novaPostBranchRef = formData.novaPostBranchRef;
      customerDetails.novaPostBranchNumber = formData.novaPostBranchNumber;
      customerDetails.novaPostPointType = isNovaPoshtaLockerDelivery(
        formData.deliveryMethod
      )
        ? "locker"
        : "branch";
      customerDetails.novaPoshtaDelivery = novaPoshtaDelivery;
    }
    if (isNovaPoshtaCourierDelivery(formData.deliveryMethod)) {
      customerDetails.city = formData.city;
      customerDetails.street = formData.street;
      customerDetails.buildingNumber = formData.buildingNumber;
      customerDetails.postalCode = formData.postalCode;
      customerDetails.apartmentNumber = formData.apartmentNumber;
      customerDetails.floor = formData.floor;
      customerDetails.novaPostCityRef =
        novaPoshtaDelivery?.city?.ref || "";
      customerDetails.novaPoshtaDelivery = novaPoshtaDelivery;
      if (formData.entranceNumber)
        customerDetails.entranceNumber = formData.entranceNumber;
      if (formData.courierNote)
        customerDetails.courierNote = formData.courierNote;
    }
    if (isKyivCourierDelivery(formData.deliveryMethod)) {
      customerDetails.city = "Київ, Київська область";
      customerDetails.street = formData.street;
      customerDetails.buildingNumber = formData.buildingNumber;
      if (formData.entranceNumber)
        customerDetails.entranceNumber = formData.entranceNumber;
      if (formData.courierNote)
        customerDetails.courierNote = formData.courierNote;
    }

    let confirmedLoyaltyQuote = loyalty?.quote || null;
    if (loyalty?.refreshQuote) {
      try {
        confirmedLoyaltyQuote = await loyalty.refreshQuote({
          bonusKopecks: loyalty.requestedBonusKopecks,
        });
        if (loyalty.quote && (confirmedLoyaltyQuote.appliedBonusKopecks !== loyalty.quote.appliedBonusKopecks || confirmedLoyaltyQuote.payableItemsKopecks !== loyalty.quote.payableItemsKopecks)) {
          setDeliveryValidationMessage("Умови замовлення змінилися. Суму перераховано. Перевірте її та повторно підтвердьте оформлення.");
          setShowErrorMessage(true);
          return;
        }
      } catch (quoteError) {
        setDeliveryValidationMessage(
          quoteError?.response?.data?.message ||
            "Не вдалося підтвердити актуальну суму та бонусний ліміт. Оновіть розрахунок і спробуйте ще раз.",
        );
        setShowErrorMessage(true);
        return;
      }
    }

    if (loyalty && !confirmedLoyaltyQuote?.hasAuthoritativeTotals) {
      setDeliveryValidationMessage(
        "Очікуємо підтверджений backend-розрахунок вартості замовлення.",
      );
      setShowErrorMessage(true);
      return;
    }

    const bonusKopecksToRedeem = getCustomerRedeemBonusKopecks({
      customer,
      quote: confirmedLoyaltyQuote,
    });

    // 📊 Аналітика: початок оформлення замовлення
    analytics.beginCheckout(cartItems, totalAmount);

    navigate("/order-summary", {
      state: {
        cartItems,
        customerDetails,
        totalAmount,
        loyaltyQuote: confirmedLoyaltyQuote,
        loyaltySettings:
          confirmedLoyaltyQuote?.settings || loyalty?.settings || null,
        bonusKopecksToRedeem,
      },
    });
  };


  // --- Loader, поки вантажиться профіль ---
  if (loading) return <div>Завантаження...</div>;

  return (
    <div className={styles.checkoutForm}>
      <div className={styles.formHead}>
        <h3 className={styles.formTitle}>Оформлення замовлення</h3>
        <p className={styles.formSubtitle}>
          Заповніть контактні дані та оберіть спосіб доставки й оплати
        </p>
      </div>
      {showErrorMessage && (
        <p className={styles.errorMessage}>Незаповнені обов'язкові поля</p>
      )}
      {deliveryValidationMessage && (
        <p className={styles.errorMessage}>{deliveryValidationMessage}</p>
      )}

      <p className={styles.sectionTitle}>Контактні дані</p>
      <label
        className={`${styles.field} ${
          validationErrors.customerName ? styles.errorLabel : ""
        }`}
      >
        Прізвище та ім’я <span className={styles.required}>*</span>
        <input
          type="text"
          value={formData.customerName}
          onChange={updateField("customerName")}
          className={validationErrors.customerName ? styles.errorInput : ""}
        />
      </label>

      <label
        className={`${styles.field} ${
          validationErrors.phone ? styles.errorLabel : ""
        }`}
      >
        Контактний телефон <span className={styles.required}>*</span>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => {
            const v = e.target.value.startsWith("+380")
              ? e.target.value
              : "+380";
            setFormData((f) => ({ ...f, phone: v }));
          }}
          className={validationErrors.phone ? styles.errorInput : ""}
        />
      </label>

      <label className={styles.field}>
        Електронна пошта
        <input
          type="email"
          value={formData.email}
          onChange={updateField("email")}
        />
      </label>

      <p className={styles.sectionTitle}>Доставка</p>
      <label
        className={`${styles.field} ${
          validationErrors.deliveryMethod ? styles.errorLabel : ""
        }`}
      >
        Вибір способу доставки <span className={styles.required}>*</span>
        <select
          value={formData.deliveryMethod}
          onChange={updateField("deliveryMethod")}
          className={validationErrors.deliveryMethod ? styles.errorInput : ""}
        >
          <option value="" disabled>
            Оберіть метод доставки
          </option>
          {enabledDeliveryOptions.map((method) => (
            <option key={method.value} value={method.value}>
              {method.label}
            </option>
          ))}
        </select>
      </label>

      {isBranchDelivery(formData.deliveryMethod) && (
        <div className={styles.novaPoshtaWrap}>
          <NovaPoshtaAutoComplete
            mode="settlement"
            value={formData.city}
            className={styles.deliveryAutoCompleteField}
            label={<>Місто <span className={styles.required}>*</span></>}
            validationError={validationErrors.city}
            onChange={(city) =>
              setFormData((current) => ({
                ...current,
                city,
                branchNumber: "",
                novaPostCityRef: "",
                novaPostBranchRef: "",
                novaPostBranchNumber: "",
                novaPoshtaDelivery: {
                  ...createEmptyNovaPoshtaDelivery(
                    getNovaPoshtaDeliveryMethod(current.deliveryMethod)
                  ),
                  city: {
                    id: null,
                    ref: "",
                    name: city,
                    regionName: "",
                    areaName: "",
                  },
                },
              }))
            }
            onSelect={(selected) => {
              if (!selected) return;
              const city = settlementFromSelection(selected);
              setFormData((current) => ({
                ...current,
                city: formatNovaPostCityLabel(city, selected.displayName),
                branchNumber: "",
                novaPostCityRef: city.ref,
                novaPostBranchRef: "",
                novaPostBranchNumber: "",
                novaPoshtaDelivery: {
                  ...createEmptyNovaPoshtaDelivery(
                    getNovaPoshtaDeliveryMethod(current.deliveryMethod)
                  ),
                  city,
                },
              }));
            }}
          />
          <NovaPoshtaAutoComplete
            mode={
              isNovaPoshtaLockerDelivery(formData.deliveryMethod)
                ? "parcelLocker"
                : "branch"
            }
            value={formData.branchNumber}
            className={styles.deliveryAutoCompleteField}
            settlement={formData.novaPoshtaDelivery?.city}
            label={
              <>
                {isNovaPoshtaLockerDelivery(formData.deliveryMethod)
                  ? "Поштомат"
                  : "Відділення"}{" "}
                Нової пошти <span className={styles.required}>*</span>
              </>
            }
            validationError={validationErrors.branchNumber}
            onChange={(branchNumber) =>
              setFormData((current) => ({
                ...current,
                branchNumber,
                novaPostBranchRef: "",
                novaPostBranchNumber: "",
                novaPoshtaDelivery: {
                  ...(current.novaPoshtaDelivery ||
                    createEmptyNovaPoshtaDelivery(
                      getNovaPoshtaDeliveryMethod(current.deliveryMethod)
                    )),
                  division: createEmptyNovaPoshtaDelivery(
                    getNovaPoshtaDeliveryMethod(current.deliveryMethod)
                  ).division,
                },
              }))
            }
            onSelect={(selected) => {
              if (!selected) return;
              const city = settlementFromSelection(selected);
              const division = divisionFromSelection(selected);
              setFormData((current) => ({
                ...current,
                city: formatNovaPostCityLabel(city),
                branchNumber: formatNovaPostDivisionLabel(division),
                novaPostCityRef: city.ref,
                novaPostBranchRef: division.ref,
                novaPostBranchNumber: division.divisionNumber,
                novaPostPointType:
                  selected.type === "parcelLocker" ? "locker" : "branch",
                novaPoshtaDelivery: {
                  apiVersion: "v.1.0",
                  provider: "nova-poshta",
                  method:
                    selected.type === "parcelLocker"
                      ? "parcelLocker"
                      : "branch",
                  serviceType: "",
                  city,
                  division,
                },
              }));
            }}
          />
        </div>
      )}

      {isCourierDelivery(formData.deliveryMethod) && (
        <>
          {isNovaPoshtaCourierDelivery(formData.deliveryMethod) && (
            <NovaPoshtaAutoComplete
              mode="settlement"
              value={formData.city}
              className={styles.deliveryAutoCompleteField}
              label={<>Місто <span className={styles.required}>*</span></>}
              validationError={validationErrors.city}
              onChange={(city) =>
                setFormData((current) => ({
                  ...current,
                  city,
                  street: "",
                  buildingNumber: "",
                  postalCode: "",
                  apartmentNumber: "",
                  entranceNumber: "",
                  floor: "",
                  novaPostCityRef: "",
                  novaPoshtaDelivery: {
                    ...createEmptyNovaPoshtaDelivery("courier"),
                    city: {
                      id: null,
                      ref: "",
                      name: city,
                      regionName: "",
                      areaName: "",
                    },
                  },
                }))
              }
              onSelect={(selected) => {
                if (!selected) return;
                const city = settlementFromSelection(selected);
                setFormData((current) => ({
                  ...current,
                  city: formatNovaPostCityLabel(city, selected.displayName),
                  novaPostCityRef: city.ref,
                  novaPoshtaDelivery: {
                    ...createEmptyNovaPoshtaDelivery("courier"),
                    city,
                  },
                }));
              }}
            />
          )}
          {isNovaPoshtaCourierDelivery(formData.deliveryMethod) ? (
            <NovaPoshtaAutoComplete
              mode="street"
              value={formData.street}
              settlement={formData.novaPoshtaDelivery?.city}
              label={<>Вулиця <span className={styles.required}>*</span></>}
              validationError={validationErrors.street}
              onChange={(street) =>
                setFormData((current) => ({ ...current, street }))
              }
              onSelect={(selected) =>
                setFormData((current) => {
                  const emptyDelivery = createEmptyNovaPoshtaDelivery("courier");
                  const selectedAddress = selected?.address;
                  const streetName = selectedAddress?.streetName || "";

                  return {
                    ...current,
                    street: streetName || current.street,
                    novaPoshtaDelivery: {
                      ...(current.novaPoshtaDelivery || emptyDelivery),
                      method: "courier",
                      courierAddress: {
                        ...(current.novaPoshtaDelivery?.courierAddress ||
                          emptyDelivery.courierAddress),
                        streetId: selectedAddress?.streetId || "",
                        streetRef: selectedAddress?.streetRef || "",
                        streetName,
                        fullAddress: streetName,
                      },
                    },
                  };
                })
              }
            />
          ) : (
            <label
              className={`${styles.field} ${
                validationErrors.street ? styles.errorLabel : ""
              }`}
            >
              Вулиця <span className={styles.required}>*</span>
              <input
                type="text"
                value={formData.street}
                onChange={updateField("street")}
                className={validationErrors.street ? styles.errorInput : ""}
              />
            </label>
          )}
          <label
            className={`${styles.field} ${
              validationErrors.buildingNumber ? styles.errorLabel : ""
            }`}
          >
            Номер будинку <span className={styles.required}>*</span>:
            <input
              type="text"
              value={formData.buildingNumber}
              onChange={updateField("buildingNumber")}
              className={
                validationErrors.buildingNumber ? styles.errorInput : ""
              }
            />
          </label>
          {isNovaPoshtaCourierDelivery(formData.deliveryMethod) && (
            <label
              className={`${styles.field} ${
                validationErrors.postalCode ? styles.errorLabel : ""
              }`}
            >
              Поштовий індекс <span className={styles.required}>*</span>
              <input
                type="text"
                inputMode="numeric"
                value={formData.postalCode}
                onChange={updateField("postalCode")}
                className={validationErrors.postalCode ? styles.errorInput : ""}
              />
            </label>
          )}
          <label className={styles.field}>
            Квартира / офіс:
            <input
              type="text"
              value={formData.apartmentNumber}
              onChange={updateField("apartmentNumber")}
            />
          </label>
          <label className={styles.field}>
            Номер під’їзду (необов’язково):
            <input
              type="text"
              value={formData.entranceNumber}
              onChange={updateField("entranceNumber")}
            />
          </label>
          <label className={styles.field}>
            Поверх:
            <input
              type="text"
              value={formData.floor}
              onChange={updateField("floor")}
            />
          </label>
          <label className={styles.field}>
            Коментар для кур’єра (необов’язково):
            <textarea
              value={formData.courierNote}
              onChange={updateField("courierNote")}
            />
          </label>
        </>
      )}

      <p className={styles.sectionTitle}>Оплата</p>
      <label
        className={`${styles.field} ${
          validationErrors.paymentMethod ? styles.errorLabel : ""
        }`}
      >
        Спосіб оплати <span className={styles.required}>*</span>
        <select
          value={formData.paymentMethod}
          onChange={updateField("paymentMethod")}
          className={validationErrors.paymentMethod ? styles.errorInput : ""}
        >
          <option value="" disabled>
            Оберіть спосіб оплати
          </option>
          {CHECKOUT_PAYMENT_METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>

      <button
        onClick={handleSubmitOrder}
        className={styles.submitOrderButton}
        disabled={
          stockInsufficient ||
          validatingDelivery ||
          Boolean(loyalty?.loading) ||
          Boolean(loyalty && !loyalty.quote)
        }
      >
        {validatingDelivery
          ? "Перевіряємо доставку…"
          : loyalty?.loading
            ? "Оновлюємо суму та бонуси…"
          : "Переглянути замовлення та оформити"}
      </button>
    </div>
  );
};

export default CheckoutForm;
